<template>
  <div class="module-block module-block-recapitulative">
    <h4>{{ $t('billingRecap.title') }}</h4>

    <div class="row module-block-offer-choice-name">
      <div class="col-sm-6 col-md-6 col-lg-2">
        <span>{{ $t('billingRecap.choiceName') }}</span>
      </div>
      <div v-if="choiceName"  class="col-sm-6 col-md-6 col-lg-10">
        <span v-html="choiceName"></span>
        <br v-if="choiceCode">
        <span v-if="choiceCode" class="text-muted">
          (<span class="font-italic">{{ $t('billingRecap.choiceCode') }}: <span>{{ choiceCode }}</span></span>)
        </span>
      </div>
      <div v-else class="col-sm-6 col-md-6 col-lg-10">
        <span>{{ choiceCode }}</span>
      </div>
    </div>

    <div v-if="choiceLongDescription" class="row module-block-offer-long-description">
      <div class="col-sm-6 col-md-6 col-lg-2">
        <span>{{ $t('billingRecap.description') }}</span>
      </div>
      <div class="col-sm-6 col-md-6 col-lg-10">
        <span v-html="choiceLongDescription"></span>
      </div>
    </div>

    <div class="row module-block-payment-at-order-time module-block-recapitulative-max-width pt-2">
      <div :class="`${choiceFreeDays ? 'col-sm-6 col-md-6 col-lg-8' : 'col-sm-6 col-md-6 col-lg-12'}`">
        <strong>{{ $t('billingRecap.paymentAtOrderTime') }}</strong>
      </div>
      <div v-if="choiceFreeDays" class="free-days-today col-sm-6 col-md-6 col-lg-4 text-right text-nowrap">
        <span>0.00</span>
        <span>{{ currencyIsoCode }}</span>
      </div>
    </div>

    <template v-if="choiceFreeDays">
      <div class="row module-block-offer-free-days module-block-recapitulative-max-width">
        <div class="free-days-next-label col-sm-6 col-md-6 col-lg-8">
          <span>{{ $t('billingRecap.paymentAfterFreeDays', {freeDays: choiceFreeDays}) }}</span>
        </div>
        <div class="free-days-next-price col-sm-6 col-md-6 col-lg-4 text-right text-nowrap">
          <span>{{ presentFloat(orderTotal) }}</span>
          <span>{{ currencyIsoCode }}</span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row module-block-offer-price module-block-recapitulative-max-width">
        <div class="col-sm-6 col-md-6 col-lg-8">
          <span>{{ $t('billingRecap.price') }}</span>
        </div>
        <div class="col-sm-6 col-md-6 col-lg-4 text-right text-nowrap">
          <span>{{ presentFloat(choicePrice) }}</span>
          <span>{{ currencyIsoCode }}</span>
        </div>
      </div>

      <div v-if="choiceShippingPrice" class="row module-block-offer-delivery module-block-recapitulative-max-width">
        <div class="col-sm-6 col-md-6 col-lg-8">
          <span>{{ shippingPriceLabel ? shippingPriceLabel : $t('billingRecap.shippingPrice') }}</span>
        </div>
        <div class="col-sm-6 col-md-6 col-lg-4 text-right text-nowrap">
          <span>{{ presentFloat(choiceShippingPrice) }}</span>
          <span>{{ currencyIsoCode }}</span>
        </div>
      </div>

      <div class="row module-block-offer-total module-block-recapitulative-max-width">
        <div class="col-sm-6 col-md-6 col-lg-8">
          <strong>{{ $t('billingRecap.orderTotal') }}</strong>
        </div>
        <div class="col-sm-6 col-md-6 col-lg-4 text-right text-nowrap font-weight-bold">
          <span>{{ presentFloat(orderTotal) }}</span>
          <span>{{ currencyIsoCode }}</span>
        </div>
      </div>
    </template>

    <div v-if="paymentMethod" class="row module-block-offer-choice-payment-method module-block-recapitulative-max-width">
      <div class="col-sm-6 col-md-6 col-lg-8">
        <span>{{ $t('billingRecap.paymentMethod') }}</span>
      </div>
      <div class="col-sm-6 col-md-6 col-lg-4 text-right text-nowrap">
        {{ Utils.capitalizeFirstLetter($t('all.' + paymentMethod)) }}
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import VanguardConfig from '../../../config/vanguard';
import Utils from '../modules/utils';
import ModalCancelOneClick from '@/common/components/modal-cancel-one-click.vue';

export default {
  components: {
    ModalCancelOneClick,
  },
  computed: {
    ...mapGetters([
      'currentChoiceConfig',
      'useSameAddressForShipping',
      'billingCountry',
      'shippingCountry'
    ]),
    orderTotal() {
      return parseFloat(this.currentChoiceConfig.price) + this.choiceShippingPrice; // Avoid float calc issues
    },
    choicePrice() {
      return parseFloat(this.currentChoiceConfig.price);
    },
    hasShippingCost() {
      return this.currentChoiceConfig.shippingAmount !== null;
    },
    choiceShippingPrice() {
      const countryCode = this.useSameAddressForShipping ? this.billingCountry : this.shippingCountry;

      let shippingAmount = 0;
      if (this.currentChoiceConfig.shippingAmount !== null && (typeof this.currentChoiceConfig.shippingAmount[countryCode] !== 'undefined')) {
        shippingAmount = parseFloat(this.currentChoiceConfig.shippingAmount[countryCode]);
      }

      return shippingAmount;
    },
    currencyIsoCode() {
      return VanguardConfig.currencyIsoCode;
    },
    choiceName() {
      if (this.currentChoiceConfig.description !== null && this.currentChoiceConfig.description !== '') {
        return this.currentChoiceConfig.description;
      }

      return null;
    },
    choiceCode() {
      if (this.currentChoiceConfig.name !== null && this.currentChoiceConfig.name !== '') {
        return this.currentChoiceConfig.name;
      }

      return null;
    },
    choiceFreeDays() {
      if (this.currentChoiceConfig.freeDays !== null && this.currentChoiceConfig.freeDays !== '' && this.currentChoiceConfig.freeDays !== "0") {
        return this.currentChoiceConfig.freeDays;
      }

      return null;
    },
    choiceLongDescription() {
      if (this.currentChoiceConfig.longDescription !== null && this.currentChoiceConfig.longDescription !== '') {
        return this.currentChoiceConfig.longDescription;
      }

      return null;
    },
    paymentMethod() {
      if (this.currentChoiceConfig.paymentMethod !== null && this.currentChoiceConfig.paymentMethod !== '') {
        return this.currentChoiceConfig.paymentMethod;
      }

      return null;
    },
    shippingPriceLabel() {
      if(this.currentChoiceConfig.shippingPriceLabel !== null && this.currentChoiceConfig.shippingPriceLabel !== '') {
        return this.currentChoiceConfig.shippingPriceLabel;
      }

      return null;
    },
    Utils() {
      return Utils;
    }
  },
  methods: {
    presentFloat(value) {
      return value.toFixed(2);
    },
  }
};
</script>



// WEBPACK FOOTER //
// billing-recap.vue?593cd0c2